/**
 * Minimal JSON Schema draft 2020-12 linter for LLM tool `input_schema`.
 *
 * Targets the failure modes we've seen Anthropic reject in practice, not
 * full spec conformance. A permissive `ajv` build ships ~40KB gzipped and
 * fights Workers' eval restrictions; a hand-rolled linter tailored to the
 * ~8 observed violations gives better error messages at ~3KB with no deps.
 *
 * Used by:
 *  - Zod `superRefine` on tool CRUD contracts (configure-time gate)
 *  - HubDO `createTool`/`updateTool` (defense-in-depth at the write boundary)
 *  - `buildAgentSettings.buildAgentToolsForLLM` (runtime drop — logs error,
 *    does not throw)
 *
 * Pure — no side effects.
 */

export const PLACEHOLDER_TOKENS = [
  '[KANBAN_STATUS_VALUES]',
  '[KANBAN_STATUS_NAMES]',
  '[SCHEDULING_STATUS_NAMES]',
  '[STATE_SLUG_VALUES]',
  // Carried by `close_conversation` as well as `update_kanban_status`, since an
  // agent close routes through the terminal transition. Omitting them here does not
  // merely weaken a hint: `containsKanbanPlaceholders` is what lets HubDO's render
  // chokepoint recognize a template config, and its other trigger is
  // `operation === 'update_kanban_status'` — so a token missing from this list means
  // `close_conversation` is never rendered and is then rejected as malformed.
  '[KANBAN_OUTCOME_VALUES]',
  '[KANBAN_OUTCOME_NAMES]',
] as const;

/**
 * True if `value` serializes to a JSON blob containing any kanban placeholder
 * token. Callers use this to skip strict JSON Schema validation on template
 * configs that the HubDO chokepoint will render before validating.
 */
export function containsKanbanPlaceholders(value: unknown): boolean {
  if (value == null) return false;
  const serialized = typeof value === 'string' ? value : JSON.stringify(value);
  for (const token of PLACEHOLDER_TOKENS) {
    if (serialized.includes(token)) return true;
  }
  return false;
}

/**
 * Parse a `tool_config` (object or JSON string) and return its `parameters`
 * field, or `undefined` if the config isn't a non-array object or doesn't
 * carry one. Shared by the HubDO chokepoint and both zod `superRefine`s so
 * they all agree on what "has a schema to validate" means.
 */
export function extractToolParameters(toolConfig: unknown): unknown {
  let cfg: unknown = toolConfig;
  if (typeof cfg === 'string') {
    try { cfg = JSON.parse(cfg); } catch { return undefined; }
  }
  if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return undefined;
  return (cfg as Record<string, unknown>).parameters;
}

const ALLOWED_TYPES = new Set(['string', 'number', 'integer', 'boolean', 'object', 'array', 'null']);

// Keyword positions whose value is itself a subschema (or a boolean schema in
// draft 2020-12). The validator MUST descend these — a structurally-invalid
// subschema buried in a composition/`$defs`/conditional keyword would otherwise
// reach the LLM provider and 400 the whole agent turn. `properties` and `items`
// have dedicated blocks below (richer error messages / type-guarded), so they
// are intentionally absent here.
const SUBSCHEMA_OBJECT_KEYWORDS = [
  'additionalProperties', 'unevaluatedProperties', 'additionalItems', 'unevaluatedItems',
  'contains', 'propertyNames', 'not', 'if', 'then', 'else',
] as const;
const SUBSCHEMA_LIST_KEYWORDS = ['allOf', 'anyOf', 'oneOf', 'prefixItems'] as const;
const SUBSCHEMA_MAP_KEYWORDS = ['$defs', 'definitions', 'patternProperties', 'dependentSchemas'] as const;

export interface SchemaViolation {
  path: string;
  message: string;
  suggestion?: string;
}

export type ValidationResult =
  | { ok: true }
  | { ok: false; errors: SchemaViolation[] };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isPrimitive(v: unknown): boolean {
  return v === null || ['string', 'number', 'boolean'].includes(typeof v);
}

function typeMatches(typeField: unknown, allowed: Set<string>): boolean {
  if (typeof typeField === 'string') return allowed.has(typeField);
  if (Array.isArray(typeField)) return typeField.every(t => typeof t === 'string' && allowed.has(t));
  return false;
}

/** Max recursion depth. Anthropic's own validator caps nesting well below
 *  this; we add our own floor to keep a pathological user-supplied schema
 *  from exhausting the Worker call stack. */
export const MAX_SCHEMA_DEPTH = 20;

function validateSchema(
  schema: unknown,
  path: string,
  errors: SchemaViolation[],
  opts: { isRoot?: boolean; depth?: number } = {},
): void {
  // A boolean is a valid subschema in draft 2020-12 (`true` = match-anything,
  // `false` = match-nothing) — legal in any nested position (`anyOf` element,
  // tuple `items` element, `$defs`/`additionalProperties` value, …). The
  // truncation sanitizer preserves these booleans verbatim, so rejecting them
  // here would silently drop an otherwise-valid tool (the sanitizer-preserves /
  // validator-rejects class this validator was hardened against). A boolean is a
  // leaf — no children, so depth is irrelevant. The object-only root constraint
  // is still enforced upstream in `validateToolInputSchema`.
  if (typeof schema === 'boolean') return;

  const depth = opts.depth ?? 0;
  if (depth > MAX_SCHEMA_DEPTH) {
    errors.push({
      path: path || '<root>',
      message: `schema nesting exceeds ${MAX_SCHEMA_DEPTH} levels`,
      suggestion: 'Flatten the schema; LLM providers reject deeply nested tool input_schemas.',
    });
    return;
  }

  if (!isRecord(schema)) {
    errors.push({
      path,
      message: `expected object, got ${schema === null ? 'null' : typeof schema}`,
    });
    return;
  }

  // Root schemas must be objects specifically (not just any allowed type).
  // This check replaces the earlier duplicate root-type check in
  // `validateToolInputSchema` — keeping both produced two errors per bad root.
  if (opts.isRoot) {
    if ('type' in schema && schema.type !== 'object') {
      errors.push({
        path: path ? `${path}.type` : 'type',
        message: `root type must be 'object', got ${JSON.stringify(schema.type)}`,
        suggestion: "Tool parameters at the root must be an object: `{ type: 'object', properties: { ... } }`.",
      });
    }
  } else if ('type' in schema && !typeMatches(schema.type, ALLOWED_TYPES)) {
    errors.push({
      path: `${path}.type`,
      message: `type must be one of ${[...ALLOWED_TYPES].join('/')} or an array of those, got ${JSON.stringify(schema.type)}`,
    });
  }

  if ('enum' in schema) {
    const e = schema.enum;
    if (typeof e === 'string') {
      const isPlaceholder = PLACEHOLDER_TOKENS.includes(e as (typeof PLACEHOLDER_TOKENS)[number]);
      // The outcome enum is NOT governed by `allowsAgentUpdate` — that flag governs
      // board moves, and closing is not a board move — so the status advice would
      // send an author to the wrong setting entirely.
      const isOutcomePlaceholder = e === '[KANBAN_OUTCOME_VALUES]';
      errors.push({
        path: `${path}.enum`,
        message: `enum must be a non-empty array of primitives, got string ${JSON.stringify(e)}`,
        suggestion: isOutcomePlaceholder
          ? 'Declare `outcomes` on the hub\'s terminal kanban status so the platform can render the placeholder into a valid array; with none configured the platform drops the parameter instead.'
          : isPlaceholder
            ? "Set `operation: 'update_kanban_status'` on the tool and ensure at least one hub.kanban_status has `allowsAgentUpdate: true` so the platform can render the placeholder into a valid array."
            : 'Wrap the value in an array: `enum: ["value"]`.',
      });
    } else if (!Array.isArray(e)) {
      errors.push({
        path: `${path}.enum`,
        message: `enum must be a non-empty array of primitives, got ${typeof e}`,
      });
    } else if (e.length === 0) {
      errors.push({
        path: `${path}.enum`,
        message: 'enum must not be empty',
      });
    } else {
      for (let i = 0; i < e.length; i++) {
        if (!isPrimitive(e[i])) {
          errors.push({
            path: `${path}.enum[${i}]`,
            message: `enum entry must be a primitive (string/number/boolean/null), got ${typeof e[i]}`,
          });
        }
      }
    }
  }

  for (const key of ['exclusiveMinimum', 'exclusiveMaximum'] as const) {
    if (key in schema && typeof schema[key] === 'boolean') {
      errors.push({
        path: `${path}.${key}`,
        message: `${key} as boolean is draft-04 syntax; draft 2020-12 requires a numeric value`,
        suggestion: `Replace with the numeric bound, e.g. \`${key}: 0\`.`,
      });
    }
  }

  if ('properties' in schema) {
    if (!isRecord(schema.properties)) {
      errors.push({
        path: `${path}.properties`,
        message: `properties must be an object, got ${Array.isArray(schema.properties) ? 'array' : typeof schema.properties}`,
      });
    } else {
      for (const [propName, propSchema] of Object.entries(schema.properties)) {
        validateSchema(propSchema, `${path}.properties.${propName}`, errors, { depth: depth + 1 });
      }
    }
  }

  if (schemaTypeIncludes(schema, 'array') && 'items' in schema) {
    // `items` straddles two forms: a single subschema (2020-12) or a tuple array
    // of subschemas (draft-07). MCP servers emit both, and the truncation
    // sanitizer preserves the tuple array — validating it as a single schema
    // would reject the whole tool ("expected object, got object") and silently
    // drop a valid tool from the agent. Validate each tuple element instead.
    if (Array.isArray(schema.items)) {
      schema.items.forEach((sub, i) => validateSchema(sub, `${path}.items[${i}]`, errors, { depth: depth + 1 }));
    } else {
      validateSchema(schema.items, `${path}.items`, errors, { depth: depth + 1 });
    }
  }

  // Descend the remaining subschema-bearing keywords (composition, `$defs`,
  // conditional, pattern/additional). Booleans are valid subschemas in draft
  // 2020-12 (e.g. `additionalProperties: false`) — only object/array forms carry
  // a nested schema, so skip the rest.
  for (const key of SUBSCHEMA_OBJECT_KEYWORDS) {
    if (key in schema && isRecord(schema[key])) {
      validateSchema(schema[key], `${path}.${key}`, errors, { depth: depth + 1 });
    }
  }
  for (const key of SUBSCHEMA_LIST_KEYWORDS) {
    const list = schema[key];
    if (Array.isArray(list)) {
      list.forEach((sub, i) => validateSchema(sub, `${path}.${key}[${i}]`, errors, { depth: depth + 1 }));
    }
  }
  for (const key of SUBSCHEMA_MAP_KEYWORDS) {
    const map = schema[key];
    if (isRecord(map)) {
      for (const [name, sub] of Object.entries(map)) {
        validateSchema(sub, `${path}.${key}.${name}`, errors, { depth: depth + 1 });
      }
    }
  }

  // Flag unresolved placeholder literals on any string-valued field, but skip
  // fields the `enum` block above already reported on — otherwise a string
  // enum set to e.g. "[KANBAN_STATUS_VALUES]" would produce two errors for
  // the same path.
  const reportedPaths = new Set(errors.map(e => e.path));
  for (const [k, v] of Object.entries(schema)) {
    if (typeof v !== 'string') continue;
    const fieldPath = `${path}.${k}`;
    if (reportedPaths.has(fieldPath)) continue;
    for (const token of PLACEHOLDER_TOKENS) {
      if (v === token) {
        errors.push({
          path: fieldPath,
          message: `unresolved placeholder ${JSON.stringify(token)}`,
          suggestion: "The platform renders this placeholder at tool-creation time. Ensure `operation: 'update_kanban_status'` is set and the hub has at least one kanban_status with `allowsAgentUpdate: true`.",
        });
      }
    }
  }
}

/**
 * True when `schema.type` is `wanted` either as the scalar value or as an
 * entry in a type-array (draft 2020-12's union-type syntax, e.g. `["array","null"]`).
 */
function schemaTypeIncludes(schema: Record<string, unknown>, wanted: string): boolean {
  const t = schema.type;
  if (typeof t === 'string') return t === wanted;
  if (Array.isArray(t)) return t.includes(wanted);
  return false;
}

// Keyword positions whose valid value is a string or array — NEVER an object.
// zod→JSON-Schema serializers sometimes emit an empty object `{}` here (e.g.
// `type:{}` for a `z.string()`, `enum:{}` for a `z.enum()`, nested inside array
// items — gh #2554). The empty object carries zero constraint yet makes the
// schema structurally INVALID, so the LLM provider 400s the turn / the tool is
// dropped at runtime — total capability loss for a tool that is otherwise fine.
// MUST stay in sync with `EMPTY_OBJECT_ARTIFACT_KEYS` in
// `workers/backend/src/utils/sanitize-mcp-tool.ts` (the ingestion-boundary
// repair); a drift-guard test pins the two behaviours together.
const EMPTY_OBJECT_ARTIFACT_KEYS = new Set(['type', 'enum', 'required']);
// Proto-pollution keys never copied onto the repaired tree. Inlined (not
// imported from the backend `nested-path` util) to keep this shared module
// dependency-free.
const REPAIR_UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
// Absolute recursion backstop. The structure-aware walk only descends recognized
// subschema positions (unknown keywords are copied verbatim, so they can't drive
// unbounded recursion), and validated schemas are already ≤ MAX_SCHEMA_DEPTH —
// but the runtime read may see rows persisted before that gate, so keep a stack
// backstop well above any real schema. Past the cap a node is returned verbatim.
const MAX_REPAIR_DEPTH = 200;

function isEmptyPlainObject(v: unknown): boolean {
  return isRecord(v) && Object.keys(v).length === 0;
}

function includesKey(keys: readonly string[], k: string): boolean {
  return keys.includes(k);
}

/**
 * Normalize known third-party JSON-Schema serialization artifacts so the result
 * is structurally valid: an empty-object value in a string/array-only KEYWORD
 * position (`type`/`enum`/`required`) — a meaningless zod→JSON-Schema artifact
 * (gh #2554) whose removal is lossless. ONLY the empty-object case is repaired; a
 * non-empty object there is ambiguous and left for `validateToolInputSchema` to
 * reject.
 *
 * The traversal MIRRORS `validateSchema` (and the ingestion-boundary
 * `capSchemaNode`): it descends ONLY genuine subschema positions, so a property
 * literally NAMED `type`/`enum`/`required` (a key inside `properties`/`$defs`/…
 * whose value is a sub-schema) is preserved, never mistaken for a keyword
 * artifact. Keyword VALUES and data (`enum` arrays, `const`, `default`,
 * `examples`, descriptions, …) are copied verbatim — unknown keywords too.
 *
 * Pure. On the clean path the result tree is NOT deep-cloned — an unchanged
 * subtree returns its original reference (structural sharing), so the per-turn hot
 * path pays a walk plus transient per-node scratch objects, not a full clone.
 * `repaired` is true iff something was dropped (`result !== schema`). Used at the
 * runtime snapshot read (`buildAgentToolsForLLM`) to heal rows persisted before
 * #2552 hardened the validator's composition-keyword descent.
 */
export function repairToolInputSchemaArtifacts(schema: unknown): { schema: unknown; repaired: boolean } {
  // Recurse a subschema-MAP value (`properties`/`$defs`/…): keys are names, values
  // are subschemas — recurse the values only; a name is never a keyword artifact.
  const repairMap = (v: unknown, depth: number): unknown => {
    if (!isRecord(v)) return v;
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [name, sub] of Object.entries(v)) {
      if (REPAIR_UNSAFE_KEYS.has(name)) { changed = true; continue; }
      const r = repairNode(sub, depth + 1);
      if (r !== sub) changed = true;
      out[name] = r;
    }
    return changed ? out : v;
  };

  // Recurse a subschema-LIST value (`anyOf`/`oneOf`/`allOf`/`prefixItems`, or a
  // draft-07 tuple `items`): an array of subschemas.
  const repairList = (arr: unknown[], depth: number): unknown[] => {
    let changed = false;
    const out = arr.map((e) => {
      const r = repairNode(e, depth + 1);
      if (r !== e) changed = true;
      return r;
    });
    return changed ? out : arr;
  };

  function repairNode(node: unknown, depth: number): unknown {
    if (depth >= MAX_REPAIR_DEPTH) return node;
    // Booleans are valid subschemas (draft 2020-12); a non-object/array in a
    // schema position is malformed server output — return verbatim (the validator
    // flags it), never descend.
    if (!isRecord(node)) return node;

    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node)) {
      if (REPAIR_UNSAFE_KEYS.has(k)) { changed = true; continue; }
      if (EMPTY_OBJECT_ARTIFACT_KEYS.has(k) && isEmptyPlainObject(v)) {
        changed = true; // drop the empty-object artifact keyword
        continue;
      }
      let nv: unknown;
      if (k === 'properties' || includesKey(SUBSCHEMA_MAP_KEYWORDS, k)) {
        nv = repairMap(v, depth);
      } else if (k === 'items') {
        nv = Array.isArray(v) ? repairList(v, depth) : repairNode(v, depth + 1);
      } else if (includesKey(SUBSCHEMA_OBJECT_KEYWORDS, k)) {
        nv = repairNode(v, depth + 1);
      } else if (includesKey(SUBSCHEMA_LIST_KEYWORDS, k)) {
        nv = Array.isArray(v) ? repairList(v, depth) : v;
      } else {
        nv = v; // keyword value / data / unknown keyword — verbatim
      }
      if (nv !== v) changed = true;
      out[k] = nv;
    }
    return changed ? out : node;
  }

  const result = repairNode(schema, 0);
  return { schema: result, repaired: result !== schema };
}

/**
 * Non-mutating structural rewrite of a JSON-Schema tree (draft 2020-12).
 *
 * Descends the SAME subschema positions `validateSchema` and
 * `repairToolInputSchemaArtifacts` recognize — via the shared `SUBSCHEMA_*`
 * taxonomy — so a rewrite (e.g. a provider-subset filter) can't miss a keyword
 * the validator accepts. Children are rewritten first (post-order); each schema
 * NODE is then handed to `transform` with its children already rewritten.
 * `transform` returns the node's replacement keyword-set (typically a filtered
 * copy of its OWN keywords) and MUST NOT recurse — descent is handled here.
 *
 * Subschema-MAP positions (`properties`/`$defs`/`patternProperties`/
 * `dependentSchemas`) recurse VALUES only, so a property/definition NAMED like a
 * keyword (e.g. a param literally named `default`) is preserved, never mistaken
 * for the keyword. List positions (`allOf`/`anyOf`/`oneOf`/`prefixItems` and the
 * draft-07 tuple `items` array) recurse each element. Booleans / non-records are
 * valid leaf subschemas and pass through verbatim. Depth-bounded + proto-safe,
 * mirroring the repair walk (kept separate: repair optimizes for structural
 * sharing + a `repaired` flag; this always returns fresh nodes).
 */
export function mapSchemaTree(
  schema: unknown,
  transform: (node: Record<string, unknown>) => Record<string, unknown>,
): unknown {
  function walk(node: unknown, depth: number): unknown {
    if (depth >= MAX_REPAIR_DEPTH) return node;
    if (!isRecord(node)) return node; // boolean / malformed leaf — verbatim

    const rewritten: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node)) {
      if (REPAIR_UNSAFE_KEYS.has(k)) continue;
      if (k === 'properties' || includesKey(SUBSCHEMA_MAP_KEYWORDS, k)) {
        if (isRecord(v)) {
          const out: Record<string, unknown> = {};
          for (const [name, sub] of Object.entries(v)) {
            if (REPAIR_UNSAFE_KEYS.has(name)) continue;
            out[name] = walk(sub, depth + 1);
          }
          rewritten[k] = out;
        } else {
          rewritten[k] = v;
        }
      } else if (k === 'items') {
        rewritten[k] = Array.isArray(v)
          ? v.map((e) => walk(e, depth + 1))
          : walk(v, depth + 1);
      } else if (includesKey(SUBSCHEMA_OBJECT_KEYWORDS, k)) {
        rewritten[k] = walk(v, depth + 1);
      } else if (includesKey(SUBSCHEMA_LIST_KEYWORDS, k)) {
        rewritten[k] = Array.isArray(v) ? v.map((e) => walk(e, depth + 1)) : v;
      } else {
        rewritten[k] = v; // keyword value / data / unknown keyword — verbatim
      }
    }
    return transform(rewritten);
  }
  return walk(schema, 0);
}

/**
 * Validate a tool's `input_schema` (the value of `parameters` on an OpenAI
 * function tool, or `input_schema` on an Anthropic custom tool).
 *
 * Returns `{ ok: true }` on success, or `{ ok: false, errors }` with one
 * entry per distinct violation. Returns multiple errors per call to let
 * the CLI render them all at once, rather than one-per-push.
 */
export function validateToolInputSchema(schema: unknown): ValidationResult {
  const errors: SchemaViolation[] = [];

  if (!isRecord(schema)) {
    errors.push({
      path: '',
      message: `expected object at root, got ${schema === null ? 'null' : typeof schema}`,
    });
    return { ok: false, errors };
  }

  // `isRoot: true` enforces the stricter "root must be object" constraint
  // inside `validateSchema` (vs the ALLOWED_TYPES set used for nested
  // schemas). Consolidating the check here avoids the previous double-report
  // at paths `type` and `.type` when the root had an invalid type.
  validateSchema(schema, '', errors, { isRoot: true });

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
